import { useState, useEffect, useRef } from "react"

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
}

export function useCountAnimation(
  target: number | null,
  duration: number = 800
): number | null {
  const [value, setValue] = useState<number | null>(null)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (target === null) {
      setValue(null)
      return
    }

    const start = performance.now()
    const from = 0
    // Animate at the target's own precision so 8.8 doesn't count up as 0..9.
    const decimals = Number.isInteger(target)
      ? 0
      : Math.min((String(target).split(".")[1] || "").length, 2)
    const factor = 10 ** decimals

    const tick = (now: number) => {
      const elapsed = now - start
      const progress = Math.min(elapsed / duration, 1)
      const eased = easeOutExpo(progress)
      const current = from + (target - from) * eased

      setValue(Math.round(current * factor) / factor)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        setValue(target)
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [target, duration])

  return value
}
